import React, { FC, useState, useContext } from "react";
import { Form, Input, Alert, Select, Button, Switch, Checkbox, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import styles from "./create-notification-form.css";
import {
  NotificationRulesQuery,
  NotifyCountQuery,
  NotifyCountQueryVariables,
} from "../../../gql/graphql";
import { PercentageInput, PriceInput } from "../input-number";
import { GlobalContext } from "~/context/global-context";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { GET_CURRENCIES, NOTIFY_COUNT_QUERY } from "~/graphql/query";
import { CurrencyPriceInput } from "../input-number/currency-price-input";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["what-section"]: whatSectionClassName,
  ["who-section"]: whoSectionClassName,
  ["filters-section"]: filtersSectionClassName,
  ["sub-title"]: subTitleClassName,
  ["sub-heading"]: subHeadingClassName,
  ["custom-form-item"]: customFormItemClassName,
  ["button-row"]: buttonRowClassName,
  alert: alertClassName,
  heading: headingClassName,
  container: containerClassName,
  ["switch-container"]: switchContainerClassName,
} = styles as any;

type NotificationRule = NotificationRulesQuery["notificationRules"][number];

type IdAndDescription = { id: number; description: string };
type Props = {
  heading: string;
  practiceAreas: { id: number; name: string }[];
  roles: IdAndDescription[];
  onCancel: () => void;
  onComplete: (values: NotificationRule) => void;
  initialValues?: Partial<NotificationRule>;
  submitButtonText?: string;
  individualsCreatorInputRender: () => React.ReactNode;
  individualsNotifyInputRender: () => React.ReactNode;
  clientsRender: () => React.ReactNode;
  mattersRender: () => React.ReactNode;
  currency: { code: string; symbol: string };
};

const selectRoleMapper = (input: IdAndDescription) => ({
  label: input.description,
  value: input.id,
});

const areasLayout = { labelCol: { span: 6 }, wrapperCol: { span: 18 } };
const rulesLayout = {
  labelCol: {
    span: 19,
    style: {
      textAlign: "left" as "left",
    },
  },
  wrapperCol: { span: 5 },
};
const notifyLayout = { labelCol: { span: 5 }, wrapperCol: { span: 20 } };

const objectsToIds = (o: { id: number }) => o.id;
const idsToObjects = (id: number) => ({ id });
const notificationFieldsToTransform: (keyof NotificationRule)[] = [
  "triggerUsers",
  "triggerFeeEarnerLevels",
  "triggerClients",
  "triggerMatters",
  "triggerOrganisationalUnits",
  "notifyOrganisationalUnits",
  "notifyUsers",
  "notifyFeeEarnerLevels",
];

export const CreateNotificationForm: FC<Props> = ({
  heading,
  clientsRender,
  mattersRender,
  practiceAreas,
  roles,
  onCancel,
  onComplete,
  initialValues = {},
  submitButtonText = "Create",
  individualsCreatorInputRender,
  individualsNotifyInputRender,
  currency,
}) => {
  const { data: currencies } = useQuery(GET_CURRENCIES);

  const taxonomy = useTaxonomyLabel();

  const isMultiCurrency = currencies?.currencies.length && currencies?.currencies.length > 1;
  const currentUserInfo = useContext(GlobalContext);
  const userOrgUnitId = currentUserInfo?.currentUser?.organisationalUnitId;

  const PA_LABEL = taxonomy.PracticeArea;

  const showGpm = React.useContext(GlobalContext).showGpm ?? false;

  const transformedInitialValues: any = {
    ...initialValues,
    currency: initialValues.currency,
  };

  for (const field of notificationFieldsToTransform) {
    transformedInitialValues[field] = ((initialValues[field] as { id: number }[]) || []).map(
      objectsToIds
    );
  }

  const [notifyFeeEarnerLevelsValue, setNotifyFeeEarnerLevelsValue] = useState<number[]>(
    transformedInitialValues.notifyFeeEarnerLevels ?? []
  );

  const practiseAreaOptions = practiceAreas.map((pa) => ({
    label: pa.name ?? "",
    value: pa.id,
  }));

  const [form] = useForm();

  const {
    data: notifyCountData,
    loading: loadingCountData,
    refetch: refetchCountData,
  } = useQuery<NotifyCountQuery, NotifyCountQueryVariables>(NOTIFY_COUNT_QUERY, {
    variables: {
      roleIds: transformedInitialValues.notifyFeeEarnerLevels ?? [],
      organisationalUnitIds: transformedInitialValues.notifyOrganisationalUnits ?? [],
      userIds: transformedInitialValues.notifyUsers ?? [],
    },
  });

  const validateForm = async (values: any) => {
    try {
      await form.validateFields();
      if (!values.notifyUsers?.length && !values.notifyFeeEarnerLevels?.length) {
        form.setFields([
          { name: "notifyUsers", errors: [""] },
          {
            name: "notifyFeeEarnerLevels",
            errors: ["Please fill out at least one field in this section."],
          },
        ]);
        return false;
      }
      if (
        !values.triggerUsers?.length &&
        !values.triggerOrganisationalUnits?.length &&
        !values.triggerFeeEarnerLevels?.length &&
        !values.triggerClients?.length &&
        !values.triggerMatters?.length
      ) {
        form.setFields([
          { name: "triggerUsers", errors: [""] },
          { name: "triggerOrganisationalUnits", errors: [""] },
          { name: "triggerFeeEarnerLevels", errors: [""] },
          { name: "triggerClients", errors: [""] },
          {
            name: "triggerMatters",
            errors: ["Please fill out at least one field in this section."],
          },
        ]);
        return false;
      }
      if (values.notifyFeeEarnerLevels?.length && !values.notifyOrganisationalUnits?.length) {
        form.setFields([
          {
            name: "notifyOrganisationalUnits",
            errors: [
              `${
                taxonomy.FeeEarnerLevel
              } alerts must have at least one ${PA_LABEL.toLowerCase()} selected.`,
            ],
          },
        ]);
        return false;
      }
      if (
        !values.proposalFeeThreshold &&
        !values.proposalGpmThreshold &&
        !values.activeGpmThreshold &&
        !values.accruedBudgetThreshold
      ) {
        form.setFields([
          { name: "proposalFeeThreshold", errors: [""] },
          { name: "proposalGpmThreshold", errors: [""] },
          { name: "activeGpmThreshold", errors: [""] },
          {
            name: "accruedBudgetThreshold",
            errors: ["Please fill out at least one field in this section."],
          },
        ]);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const clearErrorsOnChange = (changedValues: any) => {
    const notifyEntities = ["notifyUsers", "notifyFeeEarnerLevels", "notifyOrganisationalUnits"];
    const triggerEntities = [
      "triggerUsers",
      "triggerOrganisationalUnits",
      "triggerFeeEarnerLevels",
      "triggerClients",
      "triggerMatters",
    ];
    const thresholds = [
      "proposalFeeThreshold",
      "proposalGpmThreshold",
      "activeGpmThreshold",
      "accruedBudgetThreshold",
    ];
    if (notifyEntities.some((entity) => Object.keys(changedValues).includes(entity))) {
      form.setFields(notifyEntities.map((entity) => ({ name: entity, errors: [] })));
    }
    if (triggerEntities.some((entity) => Object.keys(changedValues).includes(entity))) {
      form.setFields(triggerEntities.map((entity) => ({ name: entity, errors: [] })));
    }
    if (thresholds.some((entity) => Object.keys(changedValues).includes(entity))) {
      form.setFields(thresholds.map((entity) => ({ name: entity, errors: [] })));
    }
    if (Object.prototype.hasOwnProperty.call(changedValues, "notifyOrganisationalUnits")) {
      if (!changedValues.notifyOrganisationalUnits?.length) {
        form.setFields([
          {
            name: "notifyOrganisationalUnits",
            errors: [
              `${taxonomy.FeeEarnerLevel} alerts must have at least one ${PA_LABEL} selected.`,
            ],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "notifyOrganisationalUnits",
            errors: [],
          },
        ]);
      }
    }
    if (notifyEntities.some((entity) => Object.keys(changedValues).includes(entity))) {
      refetchCountData({
        roleIds: form.getFieldValue("notifyFeeEarnerLevels") ?? [],
        organisationalUnitIds: form.getFieldValue("notifyOrganisationalUnits") ?? [],
        userIds: form.getFieldValue("notifyUsers") ?? [],
      });
    }
  };

  const onFinish = async (values: any) => {
    if (await validateForm(values)) {
      const transformedValues = { ...values };
      for (const field of notificationFieldsToTransform) {
        transformedValues[field] = (values[field] || []).map(idsToObjects);
      }
      onComplete(transformedValues);
    }
  };

  const handleNotifyFeeEarnerLevelsChange = (values: number[]) => {
    if (notifyFeeEarnerLevelsValue.length == 0 && values.length > 0 && userOrgUnitId) {
      form.setFieldsValue({
        notifyOrganisationalUnits: [userOrgUnitId],
      });
    }
    setNotifyFeeEarnerLevelsValue(values);
  };

  return (
    <div className={containerClassName}>
      <div className={headingClassName}>{heading}</div>
      <Form
        form={form}
        initialValues={transformedInitialValues}
        onValuesChange={(changedValues) => clearErrorsOnChange(changedValues)}
        onFinish={onFinish}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input placeholder="Name your alert" />
        </Form.Item>
        <Form.Item label="Toggle active / inactive" name="active" valuePropName="checked">
          <Checkbox defaultChecked />
        </Form.Item>
        <Alert type="info" message="Who are you alerting?" className={alertClassName} />
        <div className={whoSectionClassName}>
          <div>
            <Form.Item label="Individuals" name="notifyUsers" {...notifyLayout}>
              {individualsNotifyInputRender()}
            </Form.Item>
            <Form.Item
              label={`${taxonomy.FeeEarnerLevel}: `}
              {...notifyLayout}
              name="notifyFeeEarnerLevels"
            >
              <Select
                placeholder={`Select a ${taxonomy.FeeEarnerLevel}`}
                mode="multiple"
                options={roles.map(selectRoleMapper)}
                onChange={handleNotifyFeeEarnerLevelsChange}
              />
            </Form.Item>
            {notifyFeeEarnerLevelsValue.length > 0 && (
              <Form.Item label={`${PA_LABEL}s`} {...areasLayout} name="notifyOrganisationalUnits">
                <Select
                  placeholder={`Select a ${PA_LABEL}`}
                  mode="multiple"
                  options={practiseAreaOptions}
                  filterOption={(input, option) =>
                    option?.label ? option.label.toLowerCase().includes(input.toLowerCase()) : false
                  }
                />
              </Form.Item>
            )}
            {!loadingCountData && notifyFeeEarnerLevelsValue.length ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <p>
                  <WarningOutlined style={{ marginRight: "10px" }} />
                  This alert will go to {notifyCountData?.countNotifyUsers ?? 0} user
                  {notifyCountData?.countNotifyUsers !== 1 ? "s" : ""} when triggered.
                </p>
              </div>
            ) : null}
          </div>
        </div>
        <Alert type="info" message="What are you alerting?" className={alertClassName} />
        <div className={whatSectionClassName}>
          <div>
            <div className={subTitleClassName}>When this/these ...</div>
            <div className={switchContainerClassName}>
              <Form.Item valuePropName="checked" name="triggerBooleanType">
                <Switch checkedChildren="All" unCheckedChildren="Any" />
              </Form.Item>
            </div>
            <Form.Item label="Individuals" {...areasLayout} name="triggerUsers">
              {individualsCreatorInputRender()}
            </Form.Item>
            <Form.Item label={`${PA_LABEL}s`} {...areasLayout} name="triggerOrganisationalUnits">
              <Select
                placeholder={`Select ${PA_LABEL}`}
                mode="multiple"
                options={practiseAreaOptions}
                filterOption={(input, option) =>
                  option?.label ? option.label.toLowerCase().includes(input.toLowerCase()) : false
                }
              />
            </Form.Item>
            <Form.Item
              label={`${taxonomy.FeeEarnerLevel}s`}
              {...areasLayout}
              name="triggerFeeEarnerLevels"
            >
              <Select
                placeholder={`Select a ${taxonomy.FeeEarnerLevel}`}
                mode="multiple"
                options={roles.map(selectRoleMapper)}
                filterOption={(input, option) =>
                  option?.label ? option.label.toLowerCase().includes(input.toLowerCase()) : false
                }
              />
            </Form.Item>
            <Form.Item label="Clients" {...areasLayout} name="triggerClients">
              {clientsRender()}
            </Form.Item>
            <Form.Item
              label={
                <Tooltip
                  title={"Search here for the VPD proposal you wish to monitor"}
                  placement="bottom"
                >
                  <InfoCircleOutlined style={{ marginRight: 5 }} />
                  Proposals
                </Tooltip>
              }
              {...areasLayout}
              name="triggerMatters"
            >
              {mattersRender()}
            </Form.Item>
          </div>
          <div>
            <div className={subTitleClassName}>has/have...</div>
            <div className={switchContainerClassName}>
              <Form.Item valuePropName="checked" name="thresholdBooleanType">
                <Switch checkedChildren="All" unCheckedChildren="Any" />
              </Form.Item>
            </div>
            <div className={subHeadingClassName}>Fee Threshold</div>
            <Form.Item
              label="A matter with a proposed fee greater than"
              className={customFormItemClassName}
              {...rulesLayout}
              labelCol={{
                span: isMultiCurrency ? 15 : 16,
                style: {
                  textAlign: "left" as "left",
                },
              }}
              wrapperCol={{ span: isMultiCurrency ? 9 : 8 }}
            >
              <Form.Item style={{ marginBottom: "0px" }} name="proposalFeeThreshold">
                {isMultiCurrency ? (
                  <CurrencyPriceInput
                    currencies={currencies?.currencies ?? []}
                    currency={currency}
                    formKey="proposalFeeThreshold"
                  />
                ) : (
                  <PriceInput style={{ width: "100%" }} currencySymbol={currency.symbol} />
                )}
              </Form.Item>
            </Form.Item>
            {showGpm && (
              <>
                <div className={subHeadingClassName}>Gross Profit Margin</div>
                <Form.Item
                  label="A matter proposal with a Gross Profit Margin less than"
                  {...rulesLayout}
                  name="proposalGpmThreshold"
                  className={customFormItemClassName}
                >
                  <PercentageInput />
                </Form.Item>
                <Form.Item
                  name="activeGpmThreshold"
                  label="An active matter Gross Profit Margin that drops below"
                  className={customFormItemClassName}
                  {...rulesLayout}
                >
                  <PercentageInput />
                </Form.Item>
              </>
            )}
            <div className={subHeadingClassName}>Accrued Time vs Budget</div>
            <Form.Item
              name="accruedBudgetThreshold"
              label="An accrued budget on a matter that reaches"
              className={customFormItemClassName}
              {...rulesLayout}
            >
              <PercentageInput />
            </Form.Item>
          </div>
        </div>
        <Alert type="info" message="Filter Results" className={alertClassName} />
        <div className={filtersSectionClassName}>
          <Form.Item
            {...rulesLayout}
            labelCol={{ span: isMultiCurrency ? 14 : 14 }}
            wrapperCol={{ span: isMultiCurrency ? 10 : 10 }}
            label="Limit this alert to fees/accrued billable time greater than:"
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
            }}
          >
            <div>
              <Form.Item name="thresholdFilter" style={{ flex: 1, marginLeft: "10px" }}>
                {isMultiCurrency ? (
                  <CurrencyPriceInput
                    currencies={currencies?.currencies ?? []}
                    currency={currency}
                    formKey="thresholdFilter"
                  />
                ) : (
                  <PriceInput style={{ width: "100%" }} currencySymbol={currency.symbol} />
                )}
              </Form.Item>
            </div>
          </Form.Item>
        </div>
        <div className={buttonRowClassName}>
          <Button
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            Close
          </Button>
          <Button type="primary" htmlType="submit">
            {submitButtonText}
          </Button>
        </div>
      </Form>
    </div>
  );
};
